import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {InternalChatUserEntity} from "@dropDesk/domain/entities/internal_chat/users/internal_chat_user_entity";
import {InternalChatMessageEntity} from "@dropDesk/domain/entities/internal_chat/messages/internal_chat_message_entity";
import {displayDateToLocale} from "@dropDesk/utils/helpers/date_helper";

export class InternalChatEntity {
    id!: string;
    idCompany!: string;
    description!: string;
    idOpenedByUser!: string;
    openedByUser?: UserEntity;
    createdAt?: string;
    updatedAt?: string | null;
    deletedAt?: string | null;
    users?: InternalChatUserEntity[];
    lastMessageId?: string | null;
    lastMessage?: InternalChatMessageEntity | null;
    messages?: InternalChatMessageEntity[];
    countUnreadNotification?: number;
    soundPlayed?: boolean;

    constructor(data?: Partial<InternalChatEntity>) {
        Object.assign(this, data);
    }

    public isUnsaved(): boolean {
        return !this.createdAt;
    }

    public isCreator(userLogged: UserEntity): boolean {
        return this.idOpenedByUser === userLogged.id;
    }

    public hasEligibleInternalChat(user: UserEntity): boolean {
        return !!this.users?.find(_user => _user.idUser === user.id) && this.hasActiveStatus;
    }

    public get hasActiveStatus(): boolean {
        return !this.deletedAt;
    }

    public getUsersForMessage(): Record<string, boolean> {
        if (!this.users || this.users.length === 0) {
            return {};
        }

        const usersRecord: Record<string, boolean> = {};
        for (const internalChatUser of this.users) {
            if (internalChatUser.idUser) {
                usersRecord[internalChatUser.idUser] = true;
            }
        }
        return usersRecord;
    }

    public get closingServiceInternalChatHeaderText(): string | undefined {
        return this.deletedAt ? `Fim do chat interno em: ${displayDateToLocale(this.deletedAt!)}` : undefined;
    }

    public get startingServiceInternalChatHeaderText(): string | undefined {
        return this.createdAt ? `Início do chat interno em: ${displayDateToLocale(this.createdAt!)}` : undefined;
    }

    public preventDeleteRelations(oldInternalChat: InternalChatEntity): InternalChatEntity {
        this.openedByUser = this.openedByUser ?? oldInternalChat.openedByUser;
        this.users = this.users ?? oldInternalChat.users;
        this.lastMessage = this.lastMessage ?? oldInternalChat.lastMessage;
        return this;
    }

    public static fromJson(json: Record<string, any>): InternalChatEntity {
        return new InternalChatEntity({
            id: json['id'],
            idCompany: json['idCompany'],
            description: json['description'],
            idOpenedByUser: json['idOpenedByUser'],
            openedByUser: json['openedByUser'] ? UserEntity.fromJson(json['openedByUser']) : undefined,
            createdAt: json['createdAt'],
            updatedAt: json['updatedAt'],
            deletedAt: json['deletedAt'],
            users: json['users']?.map((user: any) => InternalChatUserEntity.fromJson(user)) ?? [],
            lastMessageId: json['lastMessageId'] ?? null,
            lastMessage: json['lastMessage'] ? InternalChatMessageEntity.fromJson(json['lastMessage']) : null,
            messages: json['messages']?.map((message: any) => InternalChatMessageEntity.fromJson(message)) ?? [],
            countUnreadNotification: json['countUnreadNotification'],
            soundPlayed: json['soundPlayed'] ?? false
        });
    }

    public toJson(): Record<string, unknown> {
        const ignorePropsList: string[] = ['messages', 'openedByUser', 'lastMessage', 'countUnreadNotification', 'soundPlayed'];
        const json = JSON.parse(JSON.stringify(this.copyWith(this)));
        for (const key of ignorePropsList) {
            delete json[key];
        }
        return json;
    }

    public copyWith(data?: Partial<InternalChatEntity>): InternalChatEntity {
        return new InternalChatEntity({
            id: data?.id ?? this.id,
            idCompany: data?.idCompany ?? this.idCompany,
            description: data?.description ?? this.description,
            idOpenedByUser: data?.idOpenedByUser ?? this.idOpenedByUser,
            openedByUser: data?.openedByUser ?? this.openedByUser,
            createdAt: data?.createdAt ?? this.createdAt,
            updatedAt: data?.updatedAt ?? this.updatedAt,
            deletedAt: data?.deletedAt ?? this.deletedAt,
            users: data?.users ?? this.users,
            lastMessageId: data?.lastMessageId ?? this.lastMessageId,
            lastMessage: data?.lastMessage ?? this.lastMessage,
            messages: data?.messages ?? this.messages,
            countUnreadNotification: data?.countUnreadNotification ?? this.countUnreadNotification,
            soundPlayed: data?.soundPlayed ?? this.soundPlayed
        });
    }
}
